import {
  ActionIcon,
  Divider,
  Group,
  Popover,
  Text,
  TextInput,
} from '@mantine/core';
import Button, { ButtonTypes } from '@/components/Buttons/Button';
import { Fragment, useState } from 'react';
import {
  IconDots,
  IconEdit,
  IconFlag,
  IconPencil,
  IconTrash,
} from '@tabler/icons-react';
import { isDark, timeForToday } from '@/utils/utils';
import { useClickOutside, useDisclosure } from '@mantine/hooks';

import { ACTIVITY_TYPE } from '@/consts/Activity';
import DMText from '../DMText';
import NestedCommentCard from './NestedCommentCard';
import ReportModal from '@/components/ReportModal';
import dynamic from 'next/dynamic';
import { isSignedIn } from '@/config/globalStateControl';
import themeVariables from '@/styles/themeVariables';
import { useAtomValue } from 'jotai';
import { useCommentCardStyle } from './CommentCardStyle';

const LikeButton = dynamic(() => import('@/components/Buttons/LikeButton'), {
  ssr: false,
});

const CommentCard = ({
  comment,
  nestedComment,
  handleLikeComment,
  handleAddComment,
  handleEditComment,
  handleDeleteComment,
}) => {
  const {
    id,
    status,
    text,
    user,
    created_at,
    is_mine,
    like_count,
    is_edited,
    is_liked,
  } = comment;

  const { dark } = isDark();
  const { classes } = useCommentCardStyle();

  const isLogin = useAtomValue(isSignedIn);

  const [popoverOpened, popoverHandler] = useDisclosure(false);

  const popoverRef = useClickOutside(
    () => popoverHandler.toggle(),
    ['mouseup']
  );

  const [reportModalVisible, setReportModalVisible] = useState(false);

  const [addCommentStatus, setAddCommentStatus] = useState(false);
  const [addCommentInput, setAddCommentInput] = useState('');

  const [editCommentStatus, setEditCommentStatus] = useState(false);
  const [editCommentInput, setEditCommentInput] = useState('');

  const onClickAddCommentButton = async () => {
    if (addCommentInput.length == 0) {
      return;
    }
    await handleAddComment(id, addCommentInput);
    setAddCommentStatus(false);
  };

  const onClickEditCommentButton = async () => {
    if (editCommentInput.length == 0) {
      return;
    }

    await handleEditComment(null, id, editCommentInput);
    setEditCommentStatus(false);
  };

  const renderAddCommentInput = () => (
    <TextInput
      mt={20}
      variant={'filled'}
      rightSectionWidth={120}
      disabled={!isLogin}
      placeholder={
        isLogin ? '답글을 남겨주세요.' : '로그인 후에 답글을 남겨주세요.'
      }
      className={'fadeInUpAnimation'}
      onChange={(e) => setAddCommentInput(e.target.value)}
      rightSection={
        isLogin &&
        addCommentInput.length > 0 && (
          <div
            className={classes.addCommentInputSubmitButton}
            onClick={onClickAddCommentButton}
          >
            <IconPencil size={16} />
            <DMText size={14} dColor={themeVariables.darkMode.normal}>
              답글 작성
            </DMText>
          </div>
        )
      }
      style={{
        border: dark ? '1px solid #fff' : 'none',
        borderRadius: dark ? 12 : 0,
      }}
    />
  );

  const renderCommentText = () =>
    status !== 'OPEN' ? (
      <Text italic>{text}</Text>
    ) : (
      text?.split('\n').map((line, index) => (
        <Fragment key={index}>
          {line}
          <br />
        </Fragment>
      ))
    );

  const renderCommentTextBox = () => (
    <>
      <TextInput
        defaultValue={text}
        variant={'filled'}
        rightSectionWidth={120}
        className={'fadeInUpAnimation'}
        onChange={(e) => setEditCommentInput(e.target.value)}
        style={{ color: themeVariables.darkMode.normal(dark) }}
      />
      <Group position={'right'} mt={12} spacing={4}>
        <Button
          type={ButtonTypes.transparentGray}
          onClick={() => setEditCommentStatus(false)}
          color={themeVariables.darkMode.normal(dark)}
        >
          취소
        </Button>
        <Button
          type={ButtonTypes.solidColored}
          onClick={onClickEditCommentButton}
          color={themeVariables.darkMode.normal(dark)}
        >
          댓글 수정
        </Button>
      </Group>
    </>
  );

  const renderDropdownMenu = () => (
    <Popover
      withArrow
      position={'bottom'}
      shadow={themeVariables.boxShadow.popOverShadow}
      opened={popoverOpened}
    >
      <Popover.Target>
        <ActionIcon
          className={classes.headerPopoverMenuButton}
          onClick={() => popoverHandler.toggle()}
        >
          <IconDots color={themeVariables.darkMode.normal(dark)} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown
        className={classes.headerDropdownContainer}
        onClick={() => popoverHandler.toggle()}
      >
        <div ref={popoverRef}>
          {is_mine && (
            <>
              <div
                className={classes.headerDropdownMenu}
                style={{
                  color: themeVariables.darkMode.neutral(dark),
                }}
                onClick={() => {
                  setEditCommentStatus(true);
                }}
              >
                <IconEdit size={16} />
                수정
              </div>
              <div
                className={classes.headerDropdownMenu}
                style={{
                  color: themeVariables.darkMode.neutral(dark),
                }}
                onClick={() => {
                  handleDeleteComment(id);
                }}
              >
                <IconTrash
                  size={16}
                  color={themeVariables.darkMode.normal(dark)}
                />
                삭제
              </div>
            </>
          )}
          <div
            className={classes.headerDropdownMenu}
            onClick={() => setReportModalVisible(true)}
          >
            <IconFlag size={16} color={themeVariables.darkMode.normal(dark)} />
            신고
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );

  const renderNestedComment = () =>
    nestedComment &&
    nestedComment.map((comment, idx) => (
      <NestedCommentCard
        key={idx}
        comment={comment}
        handleLikeComment={handleLikeComment}
        handleEditComment={handleEditComment}
        handleDeleteComment={handleDeleteComment}
      />
    ));

  return (
    <>
      <ReportModal
        visible={reportModalVisible}
        setVisible={setReportModalVisible}
        type={ACTIVITY_TYPE.comment}
        typeId={id}
      />
      <div className={classes.headerContainer}>
        <DMText
          className={classes.headerAuthor}
          dColor={themeVariables.darkMode.strong}
        >
          {user.nickname}
        </DMText>
        <DMText
          className={classes.headerDate}
          dColor={themeVariables.darkMode.neutral}
        >
          {timeForToday(created_at)}&nbsp;&nbsp;{is_edited && '(수정됨)'}
        </DMText>
        {renderDropdownMenu()}
      </div>
      <div
        className={classes.bodyContainer}
        style={{ color: themeVariables.darkMode.strong(dark) }}
      >
        {editCommentStatus ? renderCommentTextBox() : renderCommentText()}
      </div>
      <div
        className={classes.footerContainer}
        style={{ color: themeVariables.darkMode.strong(dark) }}
      >
        <LikeButton
          id={id}
          size={21}
          active={is_liked}
          onClick={() => handleLikeComment(id, is_liked)}
        />
        {like_count}
        <Button
          type={ButtonTypes.transparentGray}
          style={{
            height: 24,
            padding: '0px 8px',
            fontSize: 13,
            color: themeVariables.darkMode.normal(dark),
          }}
          onClick={() => setAddCommentStatus(!addCommentStatus)}
        >
          {addCommentStatus ? '작성 취소' : '답글 쓰기'}
        </Button>
      </div>
      {renderNestedComment()}
      {addCommentStatus && renderAddCommentInput()}
      <Divider my={24} color={themeVariables.colors.light300} />
    </>
  );
};

export default CommentCard;
